import React, {
  Dispatch,
  FormEventHandler,
  SetStateAction,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react'
import styled from 'styled-components/macro'
import {
  Control,
  CONTROL_STATUS,
} from '@trustero/trustero-api-web/lib/model/control_pb'
import { ModalFormId } from 'src/components/ModalForms'
import { GridColumnFlexEnd } from 'src/components/ModalForms/ModalForm.styles'
import {
  ControlModelId,
  LabelsColumn,
} from 'src/pages/Controls/IndexItem/ControlsListItem.components'
import { DepartmentChip } from 'src/pages/Controls/DepartmentChip'
import { useSearchableControls } from 'src/pages/Controls/ControlsIndexPage/ControlsIndexPage.hooks'
import {
  useControlStatuses,
  useLabelCategories,
  useLabels,
} from 'src/pages/AccountSettings/AccountSettings.hooks'
import {
  GatekeeperFlag,
  IsGatekeeperEnabled,
  useHasCustomControlStatuses,
} from 'src/context/Gatekeeper'
import { useComplianceFrameworks } from 'src/components/async/complianceframework'
import { useComplianceFrameworkObjectives } from 'src/components/async/Framework/useComplianceFrameworkObjectives'
import { getControlStatusName } from 'src/components/PageLayout/ShowPage/Dropdowns/Dropdowns.constants'
import { performMetadataSearch } from 'src/Utils/search/search'
import {
  getSearchQueryString,
  parseSearchQuery,
} from 'src/Utils/search/search.helpers'
import { getPluralization } from 'src/Utils/globalHelpers'
import palette from 'src/designSystem/variables/palette'
import { Spinner } from 'src/Throbber'
import { NONE_VALUE } from '../IndexPage/FilterBar/FilterBar.constants'
import { DropdownItemType } from '../IndexPage/FilterBar/FilterDropdowns/FilterDropdown.constants'
import { Grid, GridColumn, GridRow } from '../Grid'
import { Title } from '../Text/Title'
import { ControlPolicyInfo } from '../ListItem/ListItem.components'
import { CheckBoxInput } from '../Inputs/CheckBox/CheckBoxInput'
import { TextButton } from '../Buttons/TextButton'
import { FlexAlign, FlexRow } from '../Flex'
import {
  CloseIcon,
  SearchBar,
  SearchIcon,
  SearchInput,
  SearchInputContainer,
} from '../Search/Search.styles'
import { groupLabelsByCategory } from '../Labels/labelGroups'
import { LocalFilterDropdown } from './LocalFilterDropdown'
import {
  ControlPickerFilters,
  NO_CONTROL_PICKER_FILTERS,
  buildFrameworkFilterOptions,
  buildMoreFilterOptions,
  controlMatchesPickerFilters,
  hasControlPickerFilters,
} from './controlPicker.helpers'

const PickerHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 4px;
`

// Search fills the rest of the filter row, about an inch clear of the last filter.
const SearchSlot = styled.div`
  flex: 1;
  min-width: 200px;
  margin-left: 96px;

  ${SearchBar} {
    width: 100%;
  }
`

const PickerTitle = styled.p`
  padding-bottom: 10px;
  font-size: 14px;
  color: ${palette.neutral[900]};
`

// The list keeps one height whatever it holds, so the dialog doesn't shrink as filters
// narrow the list (which also kept the filter menus from fitting).
const ListArea = styled.div`
  height: 350px;
  overflow-y: auto;
  width: 100%;
  margin-top: 16px;
`

const ListMessage = styled.div`
  display: flex;
  justify-content: center;
  padding: 32px 0;
  text-align: center;
`

const SelectedCount = styled.span`
  margin-left: auto;
  font-size: 13px;
`

const ROW_COLUMNS =
  'min-content minmax(min-content, auto) minmax(min-content, auto) minmax(min-content, 1fr)'

type ControlPickerRowProps = {
  control: Control
  checked: boolean
  disabled?: boolean
  onToggle: (controlId: string) => void
  renderOptionalColumn?: (control: Control, isSelected: boolean) => JSX.Element
  showLabels: boolean
}

// Memoized, so toggling one control re-renders one row rather than every row in a list
// that can run to thousands. Callers keep renderOptionalColumn stable (useCallback).
const ControlPickerRow = memo(
  ({
    control,
    checked,
    disabled,
    onToggle,
    renderOptionalColumn,
    showLabels,
  }: ControlPickerRowProps): JSX.Element => {
    const controlId = control.getId()
    const controlAsObject = useMemo(() => control.toObject(), [control])
    return (
      <GridRow id={`control-picker-item-${controlId}`}>
        <GridColumn>
          <CheckBoxInput
            id={`control-picker-${controlId}`}
            aria-label={`Select ${control.getName()}`}
            checked={checked}
            onClick={(e) => e.stopPropagation()}
            onChange={() => onToggle(controlId)}
            disabled={disabled}
          />
        </GridColumn>
        <GridColumn>
          <DepartmentChip departmentId={control.getDepartmentId()} />
        </GridColumn>
        <GridColumn>
          <ControlModelId modelId={control.getModelId()} />
        </GridColumn>
        <GridColumn
          flexDirection="column"
          alignItems="flex-start"
          justifyContent="center"
        >
          <ControlPolicyInfo
            control={controlAsObject}
            isControlNa={control.getStatus() === CONTROL_STATUS.NOTAPPLICABLE}
          />
          <Title name={control.getName()} />
        </GridColumn>
        {renderOptionalColumn && (
          <GridColumnFlexEnd>
            {renderOptionalColumn(control, checked)}
          </GridColumnFlexEnd>
        )}
        {showLabels && <LabelsColumn control={controlAsObject} />}
      </GridRow>
    )
  },
)
ControlPickerRow.displayName = 'ControlPickerRow'

// Labels grouped under their categories, after a "None" option.
const useLabelFilterOptions = (): {
  options: DropdownItemType[]
  isLoading: boolean
} => {
  const { data: labelsData, isLoading: isLabelsLoading } = useLabels()
  const { data: categoriesData, isLoading: isCategoriesLoading } =
    useLabelCategories()
  const options = useMemo(() => {
    const groups = groupLabelsByCategory(
      labelsData?.getItemsList() ?? [],
      categoriesData?.getItemsList() ?? [],
    )
    return [
      NONE_VALUE,
      ...groups.flatMap((group) =>
        group.name
          ? [
              {
                value: group.name,
                label: group.name,
                nestedFilter: {
                  filterParam: group.name,
                  filterValues: group.labels,
                },
              },
            ]
          : group.labels,
      ),
    ]
  }, [labelsData, categoriesData])
  return { options, isLoading: isLabelsLoading || isCategoriesLoading }
}

type ControlPickerProps = {
  // The controls on offer; undefined while they load.
  controls: Control[] | undefined
  isLoading?: boolean
  selectedIds: string[]
  setSelectedIds: Dispatch<SetStateAction<string[]>>
  formId: ModalFormId
  onSubmit: FormEventHandler<HTMLFormElement>
  // Text shown above the filters.
  title?: string
  disabled?: boolean
  // Filters and search start over when this changes, e.g. each time the modal opens.
  resetKey?: unknown
  // An extra column at the end of each row, e.g. a status pill. Keep it stable
  // (useCallback) so rows stay memoized.
  renderOptionalColumn?: (control: Control, isSelected: boolean) => JSX.Element
  optionalColumnWidth?: string
  // Buttons shown after Select all / Deselect all, e.g. "Select failing".
  extraSelectButtons?: JSX.Element
  hideSelectButtons?: boolean
  emptyState?: JSX.Element
}

/**
 * A list of controls to pick from, with label, framework and "more" filters, a search
 * line and select / deselect all. Filters and search live in local state, not the URL,
 * so the picker can sit in a modal over any page. Select all and Deselect all act on the
 * controls the filters and search leave showing; selections made under other filters
 * are kept.
 */
export const ControlPicker = ({
  controls,
  isLoading,
  selectedIds,
  setSelectedIds,
  formId,
  onSubmit,
  title,
  disabled,
  resetKey,
  renderOptionalColumn,
  optionalColumnWidth = 'auto',
  extraSelectButtons,
  hideSelectButtons,
  emptyState,
}: ControlPickerProps): JSX.Element => {
  const [filters, setFilters] = useState<ControlPickerFilters>(
    NO_CONTROL_PICKER_FILTERS,
  )
  const [search, setSearch] = useState('')
  useEffect(() => {
    setFilters(NO_CONTROL_PICKER_FILTERS)
    setSearch('')
  }, [resetKey])

  const allControls = useMemo(() => controls ?? [], [controls])
  const showLabels = IsGatekeeperEnabled(GatekeeperFlag.LABELS)
  const { data: customStatuses } = useControlStatuses()
  const hasCustomStatuses = useHasCustomControlStatuses()
  const statusName = useCallback(
    (control: Control) =>
      getControlStatusName(
        control.getStatus(),
        control.getCustomStatusId(),
        customStatuses?.getItemsList(),
        hasCustomStatuses,
      ),
    [customStatuses, hasCustomStatuses],
  )

  const { options: labelOptions, isLoading: isLabelsLoading } =
    useLabelFilterOptions()
  const { data: frameworksData } = useComplianceFrameworks(true)
  const frameworkIds = useMemo(
    () => (frameworksData?.getItemsList() ?? []).map((cf) => cf.getId()),
    [frameworksData],
  )
  const { data: requirementsData } =
    useComplianceFrameworkObjectives(frameworkIds)
  const frameworkOptions = useMemo(
    () =>
      buildFrameworkFilterOptions(
        allControls,
        frameworksData?.getItemsList() ?? [],
        requirementsData?.getItemsList() ?? [],
      ),
    [allControls, frameworksData, requirementsData],
  )
  const moreOptions = useMemo(
    () => buildMoreFilterOptions(allControls, statusName),
    [allControls, statusName],
  )

  // Search matches the same fields as the controls index search.
  const searchable = useSearchableControls(allControls)
  const visibleControls = useMemo(() => {
    const filtered = allControls.filter((control) =>
      controlMatchesPickerFilters(control, filters, statusName),
    )
    const query = search.trim()
    if (!query) return filtered
    const { includedIds, excludedIds } = performMetadataSearch(
      searchable,
      parseSearchQuery(getSearchQueryString(query)),
    )
    const included = new Set(includedIds)
    const excluded = new Set(excludedIds)
    return filtered.filter(
      (control) =>
        included.has(control.getId()) && !excluded.has(control.getId()),
    )
  }, [allControls, filters, statusName, search, searchable])

  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds])
  const onToggle = useCallback(
    (controlId: string) =>
      setSelectedIds((previous) =>
        previous.includes(controlId)
          ? previous.filter((id) => id !== controlId)
          : [...previous, controlId],
      ),
    [setSelectedIds],
  )
  const selectVisible = (e: React.MouseEvent) => {
    e.preventDefault()
    setSelectedIds((previous) =>
      Array.from(
        new Set([...previous, ...visibleControls.map((c) => c.getId())]),
      ),
    )
  }
  const deselectVisible = (e: React.MouseEvent) => {
    e.preventDefault()
    const visible = new Set(visibleControls.map((c) => c.getId()))
    setSelectedIds((previous) => previous.filter((id) => !visible.has(id)))
  }

  const isFiltered = hasControlPickerFilters(filters) || !!search.trim()
  const countText = `${selectedIds.length} ${getPluralization(
    'control',
    selectedIds.length,
  )} selected${
    isFiltered
      ? ` · showing ${visibleControls.length} of ${allControls.length}`
      : ''
  }`

  const header = (
    <PickerHeader>
      <FlexRow gap={8} align={FlexAlign.CENTER} justify={FlexAlign.FLEX_START}>
        {showLabels && (
          <LocalFilterDropdown
            filterName="Labels"
            filterValues={labelOptions}
            isLoading={isLabelsLoading}
            selectedValues={filters.labelIds}
            onChangeSelectedValues={(labelIds) =>
              setFilters((previous) => ({ ...previous, labelIds }))
            }
            searchPlaceholder="Search labels..."
            noMatchesText="No matching labels found."
          />
        )}
        <LocalFilterDropdown
          filterName="Frameworks"
          filterValues={frameworkOptions}
          selectedValues={filters.frameworkIds}
          onChangeSelectedValues={(frameworkIds) =>
            setFilters((previous) => ({ ...previous, frameworkIds }))
          }
          searchPlaceholder="Search frameworks and requirements..."
          noMatchesText="No matching frameworks or requirements found."
          groupStyle="selectableAccordion"
        />
        <LocalFilterDropdown
          filterName="More filters"
          filterValues={moreOptions}
          selectedValues={filters.more}
          onChangeSelectedValues={(more) =>
            setFilters((previous) => ({ ...previous, more }))
          }
          searchPlaceholder="Search filters..."
          groupStyle="accordion"
        />
        {hasControlPickerFilters(filters) && (
          <TextButton
            onClick={(e) => {
              e.preventDefault()
              setFilters(NO_CONTROL_PICKER_FILTERS)
            }}
          >
            Clear filters
          </TextButton>
        )}
        <SearchSlot>
          <SearchBar>
            <SearchInputContainer>
              <SearchIcon />
              <SearchInput
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                // Enter must not submit the modal's form.
                onKeyDown={(e) => e.key === 'Enter' && e.preventDefault()}
                placeholder="Search controls"
                aria-label="Search controls"
              />
              {search.length > 0 && (
                <CloseIcon
                  role="button"
                  aria-label="Clear search"
                  onClick={() => setSearch('')}
                  // Keep focus in the search box.
                  onMouseDown={(e) => e.preventDefault()}
                />
              )}
            </SearchInputContainer>
          </SearchBar>
        </SearchSlot>
      </FlexRow>
      <FlexRow gap={16} align={FlexAlign.CENTER} justify={FlexAlign.FLEX_START}>
        {!hideSelectButtons && (
          <>
            <TextButton onClick={selectVisible} disabled={disabled}>
              Select all
            </TextButton>
            <TextButton onClick={deselectVisible} disabled={disabled}>
              Deselect all
            </TextButton>
          </>
        )}
        {extraSelectButtons}
        <SelectedCount>{countText}</SelectedCount>
      </FlexRow>
    </PickerHeader>
  )

  const columns = [
    ROW_COLUMNS,
    ...(renderOptionalColumn ? [optionalColumnWidth] : []),
    ...(showLabels ? ['auto'] : []),
  ].join(' ')

  let list: JSX.Element
  if (isLoading || !controls) {
    list = (
      <ListMessage>
        <Spinner color="primary" size="xl" />
      </ListMessage>
    )
  } else if (visibleControls.length === 0) {
    list =
      allControls.length === 0 && emptyState ? (
        emptyState
      ) : (
        <ListMessage>
          {allControls.length === 0
            ? 'There are no controls to choose from.'
            : 'No controls match these filters.'}
        </ListMessage>
      )
  } else {
    list = (
      <Grid gridTemplateColumns={columns}>
        {visibleControls.map((control) => (
          <ControlPickerRow
            key={control.getId()}
            control={control}
            checked={selectedSet.has(control.getId())}
            disabled={disabled}
            onToggle={onToggle}
            renderOptionalColumn={renderOptionalColumn}
            showLabels={showLabels}
          />
        ))}
      </Grid>
    )
  }

  return (
    <form id={formId} onSubmit={onSubmit}>
      {!!title && <PickerTitle>{title}</PickerTitle>}
      {header}
      <ListArea>{list}</ListArea>
    </form>
  )
}
